import React, { FC } from 'react'
import { Grid, Button, Paper } from '@mui/material'
import { DataGridPremium, useGridApiRef, GridPaginationModel, GridColDef } from '@mui/x-data-grid-premium'
import { HasSidebarLayout, DataLoad, ListHeader, InnerLink, TableToolbar } from 'src/components'
import { usePromptListRequest } from 'src/hooks/api'
import { useQueryParams } from 'src/hooks/ui'
import { Prompt } from 'src/api'
import { PromptColumnsDataGrid } from 'src/components/columns/prompt'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage } from 'src/config'

const columns: GridColDef<Prompt>[] = [
  PromptColumnsDataGrid.Title,
  PromptColumnsDataGrid.StartDate,
  PromptColumnsDataGrid.Flags,
  PromptColumnsDataGrid.ResponseCount,
  PromptColumnsDataGrid.CreatedAt
]

const defaultPerPage = 50
const defaultPage = 1
const initialQueryParams = {
  page: `${defaultPage}`,
  rowsPerPage: `${defaultPerPage}`,
  sort: 'createdAt:desc'
}

const PromptList: FC = () => {
  document.title = 'CLīMIT Admin Panel - Prompts'
  const apiRef = useGridApiRef()
  const [queryParams, setQueryParams] = useQueryParams(initialQueryParams)
  const { isInitialLoading, isError, data } = usePromptListRequest({ params: queryParams })
  const prompts: Prompt[] = data?.rows || []
  const count: number = data?.count || 0

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    setQueryParams(
      {
        [SearchParamsKeys.Page]: `${pagination.page + 1}`,
        [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`
      },
      [SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]
    )
  }

  const page = queryParams.page ? parseInt(queryParams.page, 10) - 1 : defaultPage - 1
  const pageSize = parseInt(queryParams.rowsPerPage, 10) || defaultPerPage

  return (
    <HasSidebarLayout>
      <DataLoad isLoading={false} isError={isError}>
        <Grid container direction="column" alignItems="stretch">
          <ListHeader title="Prompts">
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <InnerLink to="/prompts/new">
                  <Button role="link" variant="contained">
                    New Prompt
                  </Button>
                </InnerLink>
              </Grid>
            </Grid>
          </ListHeader>
        </Grid>
        <Paper sx={{ flexGrow: 1, minHeight: '200px', width: '100%', mt: 2 }}>
          <DataGridPremium<Prompt>
            apiRef={apiRef}
            pagination
            disableRowSelectionOnClick
            rows={prompts}
            columns={columns}
            rowCount={count}
            loading={isInitialLoading}
            paginationModel={{ page, pageSize }}
            rowThreshold={2}
            columnThreshold={2}
            rowHeight={65}
            paginationMode="server"
            pageSizeOptions={defaultRowsValuePerPage}
            onPaginationModelChange={handleChangePaginationModel}
            slots={{ toolbar: TableToolbar }}
            sx={{ border: 'none' }}
          />
        </Paper>
      </DataLoad>
    </HasSidebarLayout>
  )
}

export default PromptList
